import React, { useMemo } from "react";
import { ScrollView, TouchableOpacity, Text, Linking, View } from "react-native";
import LHOverlay from "../../../components/LHOverlay";
import lightStyles, {
  createHomeScreenOverlayContainerStyle,
  createHomeScreenThemeStyles,
} from "../HomeScreen.style";
import { TEXT } from "../../../constants/texts";
import CalendarIcon from "./icons/CalendarIcon";
import { useTheme } from "../../../theme";

interface EventItem {
  id: string | number;
  eventName?: string;
  shortDescription?: string;
  eventUrl?: string;
  title?: string;
  messageText?: string;
  category?: string;
  order?: number;
  link?: {
    Description?: string;
    Url?: string;
  };
  image?: {
    Description?: string;
    Url?: string;
  };
  createdDate?: string;
  modifiedDate?: string;
}

interface UpcomingEventsOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  upcomingEvents: EventItem[] | string[];
}

const UpcomingEventsOverlay: React.FC<UpcomingEventsOverlayProps> = ({
  visible,
  onDismiss,
  upcomingEvents,
}) => {
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  const overlayContainerStyle = useMemo(
    () => createHomeScreenOverlayContainerStyle(colors),
    [colors]
  );
  const handleLinkPress = (url: string, description: string) => {
    if (url) {
      Linking.openURL(url).catch((err) => {
        console.error("An error occurred opening the link:", err);
        console.log(`Failed to open \"${description}\". Please try again later.`);
      });
    } else {
      console.log(`No URL available for: ${description}`);
    }
  };

  const isEventObject = (item: EventItem | string): item is EventItem => {
    return typeof item === "object" && item !== null;
  };

  const hasData = upcomingEvents && upcomingEvents.length > 0;

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title="All Upcoming Events"
      containerStyle={overlayContainerStyle}
      headerPaddingHorizontal={24}
      hideFooter={true}
      size="large"
    >
      <ScrollView style={lightStyles.overlayContent}>
        {hasData ? (
          upcomingEvents.map((event, index) => {
            if (isEventObject(event)) {
              const url = event.eventUrl || event.link?.Url || "";
              const description =
                event.shortDescription || event.link?.Description || TEXT.COMMON.COMING_SOON;
              const displayText =
                event.eventName || event.title || event.shortDescription || "";

              return (
                <View key={event.id || index}>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    style={lightStyles.linkRow}
                    onPress={() => {
                      if (url) {
                        handleLinkPress(url, description);
                      } else {
                        console.log(`Event clicked: ${displayText}`);
                      }
                    }}
                  >
                    <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
                      <CalendarIcon width={16} height={16} />
                    </View>
                    <View style={lightStyles.linkTextContainer}>
                      <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]}>{displayText}</Text>
                      <Text style={[lightStyles.linkTextSecondary, themeStyles.linkTextSecondary]}>{description}</Text>
                    </View>
                  </TouchableOpacity>
                  <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
                </View>
              );
            }

            return (
              <View key={index}>
                <TouchableOpacity
                  activeOpacity={0.8}
                  style={lightStyles.linkRow}
                  onPress={() => console.log(`Event clicked: ${event}`)}
                >
                  <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
                    <CalendarIcon width={16} height={16} />
                  </View>
                  <View style={lightStyles.linkTextContainer}>
                    <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]}>{event}</Text>
                    <Text style={[lightStyles.linkTextSecondary, themeStyles.linkTextSecondary]}>{TEXT.COMMON.COMING_SOON}</Text>
                  </View>
                </TouchableOpacity>
                <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
              </View>
            );
          })
        ) : (
          <View style={lightStyles.noDataContainer}>
            <Text style={[lightStyles.noDataText, themeStyles.noDataText]}>{TEXT.COMMON.NO_DATA}</Text>
          </View>
        )}
      </ScrollView>
    </LHOverlay>
  );
};

export default UpcomingEventsOverlay;
